/* Unfinished drafts survive native Shopify authentication; only the server creates membership. */
(() => {
  'use strict';
  if (customElements.get('ron-access-enrollment')) return;
  class RonAccessEnrollment extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.strings = JSON.parse(this.querySelector('[data-strings]').textContent);
      this.api = window.RonAccessAPI.create({ basePath: this.dataset.apiPath });
      this.draftKey = 'ron.access.enrollment.draft.v1';
      this.step = 0;
      this.busy = false;
      this.authenticated = false;
      this.identityChecked = false;
      this.restoreDraft();
      this.querySelectorAll('[data-access-link]').forEach(link => { link.href = window.RonAccessAPI.withPreview(link.getAttribute('href')); });
      this.querySelector('[data-next]').addEventListener('click', () => this.advance());
      this.querySelector('[data-back]').addEventListener('click', () => this.showStep(this.step - 1));
      this.querySelector('[data-finish]').addEventListener('click', () => this.enroll());
      this.querySelector('[data-retry]').addEventListener('click', () => this.checkIdentity());
      this.querySelector('[data-login]').addEventListener('click', () => this.authenticate());
      this.addEventListener('input', event => {
        if (event.target.matches('[data-brand-search]')) this.filterBrands();
        if (event.target.matches('[data-value="sms_updates"]')) this.syncPhone();
        this.saveDraft();
      });
      this.addEventListener('change', event => {
        if (event.target.matches('[data-value="shoe_sizing_system"]')) this.updateShoeSizes();
        this.saveDraft();
      });
      this.addEventListener('keydown', event => {
        if (event.key === 'Enter' && event.target.matches('input:not([type="checkbox"]):not([type="search"])')) {
          event.preventDefault();
          if (this.step < 3) this.advance();
        }
      });
      this.onPageShow = event => { if (event.persisted) window.location.reload(); };
      window.addEventListener('pageshow', this.onPageShow);
      this.showStep(this.step, false);
      this.syncPhone();
      this.checkIdentity();
    }
    disconnectedCallback() { window.removeEventListener('pageshow', this.onPageShow); }
    field(key) { return this.querySelector(`[data-value="${key}"]`); }
    status(key, kind = 'status') {
      const node = this.querySelector('[data-status]');
      node.textContent = key ? this.strings[key] || this.strings.saveFailed : '';
      node.hidden = !key;
      node.setAttribute('role', kind === 'error' ? 'alert' : 'status');
      node.dataset.kind = kind;
    }
    setBusy(value) {
      this.busy = value;
      this.setAttribute('aria-busy', String(value));
      this.querySelectorAll('[data-next], [data-back], [data-finish], [data-retry], [data-login]').forEach(button => { button.disabled = value; });
      this.querySelectorAll('[data-panel] [data-fields]').forEach(fields => { fields.disabled = value || Number(fields.closest('[data-panel]').dataset.panel) !== this.step; });
    }
    async checkIdentity() {
      this.completed = false;
      this.querySelector('[data-complete]').hidden = true;
      this.querySelector('[data-experience]').hidden = false;
      this.setBusy(true);
      this.status('checking');
      this.querySelector('[data-retry]').hidden = true;
      this.querySelector('[data-login]').hidden = true;
      try {
        const result = await this.api.getMember();
        this.authenticated = true;
        this.identityChecked = true;
        if (result.member) { this.complete(result.member, false); return; }
        const email = this.dataset.customerEmail;
        if (email) this.field('email').value = email;
        this.field('email').readOnly = true;
        this.field('email').required = false;
        this.field('email').placeholder = this.strings.verifiedAccount;
        if (!this.field('first_name').value && this.dataset.customerName) this.field('first_name').value = this.dataset.customerName;
        this.status('verified');
        if (this.resumeAfterAuth) {
          this.resumeAfterAuth = false;
          this.showStep(Math.max(1, this.resumeStep || this.step), false);
          this.resumeStep = 0;
        }
      } catch (error) {
        this.authenticated = false;
        this.identityChecked = error.status === 401;
        if (error.status === 401) {
          this.resumeStep = Math.max(this.resumeStep || 0, this.step);
          this.field('email').readOnly = false;
          this.field('email').required = true;
          this.status(this.dataset.signedIn === 'true' ? 'identityMissing' : 'signInHelp');
          this.showStep(0, false);
          this.querySelector('[data-login]').hidden = this.dataset.signedIn !== 'true';
        } else {
          this.status('unavailable', 'error');
          this.querySelector('[data-retry]').hidden = false;
          // Native Shopify login still works when the membership service is unavailable.
          this.querySelector('[data-login]').hidden = this.dataset.signedIn === 'true';
        }
      } finally {
        this.setBusy(false);
        if (!this.identityChecked) this.querySelector('[data-next]').disabled = true;
        this.saveDraft();
      }
    }
    showStep(step, focus = true) {
      this.step = Math.max(0, Math.min(3, step));
      this.querySelectorAll('[data-panel]').forEach(panel => { panel.hidden = Number(panel.dataset.panel) !== this.step; });
      this.querySelectorAll('[data-step-marker]').forEach(marker => {
        if (Number(marker.dataset.stepMarker) === Math.min(this.step, 2)) marker.setAttribute('aria-current', 'step');
        else marker.removeAttribute('aria-current');
      });
      this.querySelector('[data-back]').hidden = this.step === 0;
      this.querySelector('[data-next]').hidden = this.step === 3;
      this.querySelector('[data-finish]').hidden = this.step !== 3;
      this.querySelector('[data-continue-label]').hidden = this.step === 2;
      this.querySelector('[data-review-label]').hidden = this.step !== 2;
      this.setBusy(this.busy);
      if (this.step === 3) this.renderReview();
      if (focus) this.querySelector(`[data-panel="${this.step}"] h1, [data-panel="${this.step}"] h2`)?.focus();
      this.saveDraft();
    }
    validStep() {
      const fields = [...this.querySelectorAll(`[data-panel="${this.step}"] input, [data-panel="${this.step}"] select`)];
      const invalid = fields.find(field => !field.disabled && !field.checkValidity());
      if (invalid) { invalid.reportValidity(); return false; }
      if (this.step === 0 && !this.field('first_name').value.trim()) { this.field('first_name').focus(); return false; }
      return true;
    }
    advance() {
      if (this.busy || !this.identityChecked || !this.validStep()) return;
      if (!this.authenticated) { this.authenticate(); return; }
      this.status('');
      this.showStep(this.step + 1);
    }
    authenticate() {
      if (this.busy) return;
      if (!this.field('email').readOnly && !this.field('email').reportValidity()) return;
      this.resumeAfterAuth = true;
      this.resumeStep = Math.max(1, this.resumeStep || this.step);
      this.saveDraft();
      this.setBusy(true);
      this.status('redirecting');
      const returnTo = window.location.pathname + window.location.search;
      window.location.assign(window.RonAccessAPI.getLoginUrl(returnTo, this.field('email').value.trim()));
    }
    values() {
      return {
        profile: {
          name: this.field('first_name').value.trim(),
          favoriteBrands: [...this.querySelectorAll('[data-brand]:checked')].map(input => input.value),
          clothingSize: this.field('apparel_size').value,
          shoeSize: this.field('shoe_size').value,
          shoeSizingSystem: this.field('shoe_sizing_system').value,
          savedProductIds: []
        },
        consents: {
          email: { subscribed: this.field('email_updates').checked },
          sms: { subscribed: this.field('sms_updates').checked, phone: this.field('sms_updates').checked ? this.field('phone').value.trim() : '' }
        }
      };
    }
    syncPhone() {
      const checked = this.field('sms_updates').checked;
      this.querySelector('[data-phone-field]').hidden = !checked;
      this.field('phone').required = checked;
      this.field('phone').disabled = !checked;
    }
    updateShoeSizes(keepValue = '') {
      const select = this.field('shoe_size');
      const old = keepValue || select.value;
      const system = this.field('shoe_sizing_system').value;
      const min = system === 'EU' ? 32 : 3;
      const max = system === 'EU' ? 50 : 18;
      select.replaceChildren(new Option(this.strings.noPreference, ''));
      for (let size = min; size <= max; size += 0.5) select.add(new Option(String(size), String(size)));
      select.value = [...select.options].some(option => option.value === old) ? old : '';
    }
    renderReview() {
      const values = this.values();
      const labels = { US_MEN: this.strings.usMen, US_WOMEN: this.strings.usWomen, UK: 'UK', EU: 'EU' };
      const review = {
        first_name: values.profile.name,
        email: this.dataset.customerEmail || this.strings.verifiedAccount,
        brands: values.profile.favoriteBrands.join(', '),
        apparel_size: values.profile.clothingSize,
        shoe_size: values.profile.shoeSize ? `${values.profile.shoeSize} · ${labels[values.profile.shoeSizingSystem]}` : '',
        email_updates: values.consents.email.subscribed ? this.strings.yes : this.strings.no,
        sms_updates: values.consents.sms.subscribed ? `${this.strings.yes} · ${values.consents.sms.phone}` : this.strings.no
      };
      this.querySelectorAll('[data-review]').forEach(node => { node.textContent = review[node.dataset.review] || this.strings.noPreference; });
    }
    async enroll() {
      if (this.busy || !this.authenticated || this.step !== 3) return;
      const payload = this.values();
      const fingerprint = JSON.stringify(payload);
      if (!this.pending || this.pending.fingerprint !== fingerprint) this.pending = { key: crypto.randomUUID(), fingerprint };
      this.saveDraft();
      this.setBusy(true);
      this.status('saving');
      try {
        const result = await this.api.enroll(payload, { idempotencyKey: this.pending.key });
        this.complete(result.member, true);
      } catch (error) {
        if (error.status === 401) {
          this.authenticated = false;
          this.resumeAfterAuth = true;
          this.status('sessionExpired', 'error');
          this.querySelector('[data-login]').hidden = false;
        } else this.status(error.status === 422 ? 'validationFailed' : 'saveFailed', 'error');
      } finally { this.setBusy(false); }
    }
    complete(member, justEnrolled) {
      if (!member || member.status !== 'active') throw new window.RonAccessAPI.AccessError('INVALID_RESPONSE');
      this.completed = true;
      try { sessionStorage.removeItem(this.draftKey); } catch (_) { /* Storage can be disabled. */ }
      this.querySelector('[data-experience]').hidden = true;
      const confirmation = this.querySelector('[data-complete]');
      confirmation.hidden = false;
      confirmation.querySelector('h2').textContent = justEnrolled ? this.strings.enrolled : this.strings.returning;
      confirmation.querySelector('[data-confirmation]').textContent = justEnrolled ? this.strings.enrolledHelp : this.strings.returningHelp;
      this.querySelector('[data-status]').hidden = true;
      this.querySelector('[data-retry]').hidden = true;
      this.querySelector('[data-login]').hidden = true;
      confirmation.querySelector('h2').focus();
      // The explicit portal link keeps the confirmed result readable and accessible.
    }
    filterBrands() {
      const query = this.querySelector('[data-brand-search]').value.trim().toLowerCase();
      let visible = 0;
      this.querySelectorAll('[data-brand-choice]').forEach(choice => { choice.hidden = !choice.dataset.brandChoice.includes(query); if (!choice.hidden) visible++; });
      this.querySelector('[data-brand-empty]').hidden = visible !== 0;
    }
    saveDraft() {
      if (this.completed) return;
      try {
        sessionStorage.setItem(this.draftKey, JSON.stringify({
          version: 1, expiresAt: Date.now() + 30 * 60 * 1000,
          step: this.step, resumeAfterAuth: Boolean(this.resumeAfterAuth), resumeStep: this.resumeStep || 0,
          email: this.field('email').value, ...this.values(), pending: this.pending || null
        }));
      } catch (_) { /* A blocked draft store must never count as a saved membership. */ }
    }
    restoreDraft() {
      this.updateShoeSizes();
      try {
        const draft = JSON.parse(sessionStorage.getItem(this.draftKey) || 'null');
        if (!draft || draft.version !== 1 || draft.expiresAt < Date.now()) { sessionStorage.removeItem(this.draftKey); return; }
        const profile = draft.profile || {};
        this.field('first_name').value = String(profile.name || '').slice(0, 100);
        this.field('email').value = String(draft.email || '').slice(0, 254);
        this.field('apparel_size').value = profile.clothingSize || '';
        this.field('shoe_sizing_system').value = profile.shoeSizingSystem || 'US_MEN';
        this.updateShoeSizes(profile.shoeSize || '');
        this.querySelectorAll('[data-brand]').forEach(input => { input.checked = Array.isArray(profile.favoriteBrands) && profile.favoriteBrands.includes(input.value); });
        this.field('email_updates').checked = draft.consents?.email?.subscribed === true;
        this.field('sms_updates').checked = draft.consents?.sms?.subscribed === true;
        this.field('phone').value = String(draft.consents?.sms?.phone || '').slice(0, 16);
        this.step = Number.isInteger(draft.step) ? Math.max(0, Math.min(3, draft.step)) : 0;
        this.resumeAfterAuth = draft.resumeAfterAuth === true;
        this.resumeStep = Number.isInteger(draft.resumeStep) ? Math.max(0, Math.min(3, draft.resumeStep)) : 0;
        if (draft.pending && typeof draft.pending.key === 'string' && typeof draft.pending.fingerprint === 'string') this.pending = draft.pending;
      } catch (_) { /* Corrupt drafts are ignored; the backend remains authoritative. */ }
    }
  }
  customElements.define('ron-access-enrollment', RonAccessEnrollment);
})();
